import { useCallback, useState } from 'react';

/**
 * Draft state for an inline-editable field whose committed value also arrives via
 * sync (PowerSync). Adopts a changed `synced` value while the field is idle, so a
 * collaborator's live edit lands without a reload — but never overwrites the field
 * mid-edit: a field the user is actively typing in keeps their keystrokes and
 * their value wins on blur (last-write-wins, matching these fields' semantics).
 *
 * "Actively typing" means focused AND dirty (the user has changed the draft).
 * Merely holding focus without editing is NOT protected: a collaborator's value
 * that arrives while the field is focused-but-untouched is still adopted, so
 * blurring the field can't silently re-commit the stale draft over their edit.
 *
 * Wire `onFocus`/`onBlur` into the input alongside its existing commit-on-blur
 * handler — they only track focus, they don't replace the commit work.
 */
export function useSyncedDraft<T>(synced: T) {
  const [draft, setDraftState] = useState<T>(synced);
  const [seenSynced, setSeenSynced] = useState<T>(synced);
  const [focused, setFocused] = useState(false);
  const [dirty, setDirty] = useState(false);

  // React's "adjust state during render" pattern: reconcile the draft when the
  // synced value changes, guarded by seenSynced so it can't loop. Advancing
  // seenSynced even when we don't adopt means a value that arrived mid-edit is
  // consumed, not re-applied on blur — the user's in-progress value stands.
  if (synced !== seenSynced) {
    setSeenSynced(synced);
    if (!focused || !dirty) setDraftState(synced);
  }

  // Wrap setDraft so any user edit marks the field dirty (and thus protected
  // while focused). Reconcile uses the raw setter above so it never marks dirty.
  const setDraft = useCallback((value: T) => {
    setDraftState(value);
    setDirty(true);
  }, []);

  const onFocus = () => setFocused(true);
  const onBlur = () => {
    setFocused(false);
    setDirty(false);
  };

  return { draft, setDraft, onFocus, onBlur };
}
